import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import Select from 'react-select';
import { useTranslate } from '../TranslationText';

/**
 * Clean select dropdown with React Hook Form integration.
 * Uses react-select directly (no NetaqInput wrapper).
 *
 * @param {string} name - Form field name
 * @param {string} label - Label text
 * @param {Array} options - Options array: [{ text: 'Label', value: 'id' }, ...]
 * @param {boolean} required - Show required asterisk
 * @param {boolean} disabled - Disable select
 * @param {object} rules - Validation rules
 * @param {boolean} isClearable - Allow clearing selection
 * @param {boolean} isMulti - Allow multiple selections
 * @param {boolean} hideLabel - Hide the label
 * @param {function} onChangeCallback - Optional callback after value changes
 * @param {string} placeholder - Select placeholder
 * @param {any} value - The value when used in standalone mode (no react-hook-form)
 * @param {function} onChange - The onChange handler when used in standalone mode
 */
export default function FormSelect({
    name,
    label,
    options = [],
    required = false,
    disabled = false,
    rules,
    isClearable = false,
    isMulti = false,
    hideLabel = false,
    onChangeCallback,
    placeholder,
    containerClass = "mb-2",
    noValidate = false,
    value,
    onChange,
    noMargin = false,
}) {
    const formContext = useFormContext();
    const isStandalone = !formContext;
    const { control, formState: { errors } = {} } = formContext || {};

    const { translate } = useTranslate();

    const validationRules = {
        ...(required && !noValidate && !isStandalone && {
            required: translate('web.requiredField', 'Required Field'),
        }),
        ...rules,
    };

    // Normalize options to react-select format { label, value }
    const selectOptions = options.map((opt) => ({
        label: opt.text ?? opt.label ?? opt.name,
        value: opt.value ?? opt.id,
        ...opt,
    }));

    const fieldError = isStandalone ? null : name.split('.').reduce((obj, key) => obj?.[key], errors);

    const renderSelect = (currentValue, onChangeHandler, onBlurHandler = null, fieldRef = null) => {
        // Find selected option(s) from current value (with case-insensitive fallback for GUIDs)
        const selectedValue = isMulti
            ? selectOptions.filter((opt) =>
                Array.isArray(currentValue)
                    ? currentValue.includes(opt.value)
                    : false
            )
            : selectOptions.find(
                (opt) =>
                    opt.value === currentValue ||
                    (currentValue != null &&
                        opt.value != null &&
                        String(opt.value).toLowerCase() === String(currentValue).toLowerCase())
            ) || null;

        return (
            <Select
                inputId={name}
                name={name}
                ref={fieldRef}
                options={selectOptions}
                value={selectedValue}
                onChange={(selected) => {
                    const newValue = isMulti
                        ? (selected || []).map((s) => s.value)
                        : selected?.value ?? (isClearable ? '' : null);
                    if (onChangeHandler) onChangeHandler(newValue);
                    if (onChangeCallback) onChangeCallback(selected);
                }}
                onBlur={onBlurHandler}
                isDisabled={disabled}
                isClearable={isClearable}
                isMulti={isMulti}
                isSearchable
                maxMenuHeight={175}
                placeholder={placeholder ?? translate('web.select', 'Select')}
                noOptionsMessage={() => translate('web.noOptions', 'No Options')}
                className={`max-hight-36${fieldError ? ' is-invalid' : ''}`}
                styles={{
                    control: (base, state) => ({
                        ...base,
                        minHeight: '38px', // Match Bootstrap form-control height
                        borderColor: state.isFocused ? '#86b7fe' : '#dee2e6',
                        boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none',
                        '&:hover': {
                            borderColor: state.isFocused ? '#86b7fe' : '#dee2e6'
                        },
                        // Match Bootstrap's form-control:disabled exactly
                        ...(state.isDisabled
                            ? {
                                backgroundColor: '#e9ecef',
                                borderColor: '#cccccc',
                                color: '#2c2c2a',
                                opacity: 1
                            }
                            : {
                                backgroundColor: '#ffffff'
                            }),
                        ...(fieldError ? { borderColor: '#dc3545' } : {}),
                    }),
                    singleValue: (base) => ({
                        ...base,
                        color: '#2c2c2a', // Match form-control text color
                        fontSize: '14px',
                    }),
                    placeholder: (base, state) => ({
                        ...base,
                        color: state.isDisabled ? '#2c2c2a' : '#6c757d', // Match placeholder color
                    }),
                    input: (base) => ({
                        ...base,
                        color: '#2c2c2a',
                    }),
                    dropdownIndicator: (base, state) => ({
                        ...base,
                        color: state.isDisabled ? '#adb5bd' : '#cccccc',
                    }),
                    indicatorSeparator: (base, state) => ({
                        ...base,
                        backgroundColor: state.isDisabled ? '#dee2e6' : '#cccccc',
                    })
                }}
            />
        );
    };

    return (
        <div
            id={`field-container-${name}`}
            data-field={name}
            className={noMargin ? '' : containerClass}
        >
            {!hideLabel && label && (
                <label
                    htmlFor={name}
                    className="capatalize_text"
                    style={{ fontSize: '12px', fontWeight: 'bold', textTransform: 'uppercase', color: '#6c757d' }}
                >
                    {label}{' '}
                    {required && <i style={{ color: 'red', margin: 'auto 5px' }}>*</i>}
                </label>
            )}

            {isStandalone ? (
                renderSelect(value, onChange)
            ) : (
                <Controller
                    name={name}
                    control={control}
                    rules={validationRules}
                    render={({ field }) => renderSelect(field.value, field.onChange, field.onBlur, field.ref)}
                />
            )}

            {fieldError && (
                <div className="text-danger" style={{ fontSize: 12, marginTop: 4 }}>
                    {fieldError.message || translate('web.requiredField', 'Required Field')}
                </div>
            )}
        </div>
    );
}